/**
 * NSTemplateSetup
 * ────────────────────────────────────────────────────────────────────────────
 * The two-step NetSuite template setup gate:
 *
 *   Step 1 — Fetch validations. Pulls every NetSuite lookup (vendors, items,
 *            subsidiaries, locations, currencies, terms, shipping terms — plus
 *            resellers for a Vendor Bid) and detects this account's active
 *            fields FOR THIS DOCUMENT TYPE, storing all of it locally. Reports
 *            exactly what was stored.
 *
 *   Step 2 — Field mapping. Upload a sample document of that type so AI can map
 *            its fields to NetSuite keys.
 *
 * Why the order matters: mapping a document to NetSuite fields is only as good
 * as the field set it maps against. Running step 2 first means mapping against
 * whatever happened to be cached — or nothing at all on a fresh template. Step 1
 * makes the field set explicit, current, and visible before any mapping happens.
 *
 * Step 2 is gated on step 1 having stored at least one field rule. That gate is
 * intentional, but it is never permanent: `onSkip` lets an admin proceed anyway,
 * so a NetSuite outage cannot leave someone unable to configure a template.
 *
 * Props:
 *   templateId        – the template being set up
 *   nsEntityType      – 'purchase_order' | 'vendor_bid'; selects which fields are
 *                       detected and what every label on this screen calls them
 *   defaultSubsidiary – company/subsidiary id chosen at creation, scopes detection
 *   onReady           – callback({ validations, lookups }) once step 1 succeeds
 *   onSkip            – callback() when the user chooses to bypass the gate
 *   renderStepTwo     – () => ReactNode, the mapping UI to reveal after step 1
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Database,
  Download,
  Lock,
  RefreshCw,
} from 'lucide-react'
import { nsApi } from '../services/api'
import { nsEntityNouns, nsEntityDisplayName } from '../utils/nsEntityLabels'

const LOOKUP_LABELS = {
  vendors: 'Vendors',
  items: 'Items',
  subsidiaries: 'Companies',
  locations: 'Locations',
  currencies: 'Currencies',
  terms: 'Payment terms',
  shipping_terms: 'Shipping terms',
  customers: 'Resellers',
}

function StepHeader({ number, title, state, children }) {
  // state: 'done' | 'active' | 'locked'
  const ring =
    state === 'done'
      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
      : state === 'active'
        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
        : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'

  return (
    <div className="flex items-start gap-3">
      <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${ring}`}>
        {state === 'done' ? <CheckCircle2 className="h-4 w-4" />
          : state === 'locked' ? <Lock className="h-3.5 w-3.5" />
            : number}
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-semibold ${state === 'locked'
          ? 'text-gray-400 dark:text-gray-500'
          : 'text-gray-900 dark:text-gray-100'}`}>
          {title}
        </p>
        {children}
      </div>
    </div>
  )
}

export default function NSTemplateSetup({
  templateId,
  nsEntityType = 'purchase_order',
  defaultSubsidiary,
  onReady,
  onSkip,
  renderStepTwo,
}) {
  // Every label on this screen follows the template's document type — a Vendor
  // Bid template must never be told it is fetching "purchase-order fields" or
  // asked to upload a "PO PDF".
  const nouns = nsEntityNouns(nsEntityType)
  const docName = nsEntityDisplayName(nsEntityType)
  const [status, setStatus] = useState(null)      // field-rules freshness
  const [loading, setLoading] = useState(true)
  const [fetching, setFetching] = useState(false)
  const [result, setResult] = useState(null)      // bootstrap result
  const [error, setError] = useState('')
  const [skipped, setSkipped] = useState(false)

  // Existing state, so a template that was already set up does not force the
  // user through step 1 again (auto-detection on creation may have done it).
  const loadStatus = useCallback(() => {
    if (!templateId) return
    setLoading(true)
    nsApi.getFieldRulesStatus(templateId)
      .then(res => { setStatus(res.data || null); setError('') })
      .catch(() => setStatus(null))
      .finally(() => setLoading(false))
  }, [templateId])

  useEffect(() => { loadStatus() }, [loadStatus])

  const storedCount = result?.validations?.rules_stored ?? status?.rule_count ?? 0
  const stepOneDone = storedCount > 0
  const stepTwoUnlocked = stepOneDone || skipped

  // Tell the parent as soon as step 1 is satisfied — including when it was
  // already satisfied before this component mounted. Auto-detection runs in the
  // background at template creation, so a template can arrive here already set
  // up, and forcing the user to re-fetch just to unlock step 2 would be busywork.
  const notifiedRef = React.useRef(false)
  useEffect(() => {
    if (stepOneDone && !notifiedRef.current) {
      notifiedRef.current = true
      onReady?.(result || { validations: { rules_stored: storedCount } })
    }
  }, [stepOneDone, storedCount, result, onReady])

  const handleFetch = async () => {
    setFetching(true)
    setError('')
    try {
      const res = await nsApi.bootstrapTemplate(templateId, defaultSubsidiary)
      setResult(res.data)
      // onReady is fired by the effect above once storedCount goes positive —
      // one notification path, so a re-fetch cannot double-notify.
      loadStatus()
    } catch (err) {
      const d = err?.response?.data
      // 422 still carries a result body with per-entity counts — showing the
      // partial outcome is far more useful than a bare error string.
      if (d?.lookups || d?.validations) setResult(d)
      setError(
        d?.validations?.error || d?.error ||
        'Could not fetch from NetSuite. Check the connection and role permissions.',
      )
    } finally {
      setFetching(false)
    }
  }

  const lookupRows = useMemo(() => {
    const l = result?.lookups
    if (!l) return []
    return Object.keys(LOOKUP_LABELS)
      .filter(k => k in l)
      .map(k => ({ key: k, label: LOOKUP_LABELS[k], count: l[k] }))
  }, [result])

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 p-4">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking NetSuite setup…
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* ── Step 1 ─────────────────────────────────────────────────────────── */}
      <div className={`rounded-xl border p-4 ${stepOneDone
        ? 'border-green-200 dark:border-green-800 bg-green-50/40 dark:bg-green-900/10'
        : 'border-purple-200 dark:border-purple-800 bg-purple-50/40 dark:bg-purple-900/10'}`}>
        <StepHeader
          number={1}
          title="Fetch NetSuite data & validations"
          state={stepOneDone ? 'done' : 'active'}
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
            Loads {nsEntityType === 'vendor_bid'
              ? 'suppliers, resellers and items'
              : 'vendors, items, companies, locations, currencies and terms'}, and
            detects which {nouns.hyphenated} fields this account actually requires.
            Everything is stored locally so document review validates instantly.
          </p>

          {stepOneDone && !result && (
            <p className="text-xs text-green-700 dark:text-green-300 mt-2">
              <strong>{storedCount}</strong> validation{storedCount !== 1 ? 's' : ''} already stored
              {status?.age_hours != null && ` · updated ${status.age_hours}h ago`}
              {status?.manual_count > 0 && ` · ${status.manual_count} set manually`}
            </p>
          )}

          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={handleFetch}
              disabled={fetching}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold disabled:opacity-50 transition-colors"
            >
              {fetching
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Fetching from NetSuite…</>
                : stepOneDone
                  ? <><RefreshCw className="h-3.5 w-3.5" /> Re-fetch</>
                  : <><Download className="h-3.5 w-3.5" /> Fetch now</>}
            </button>
            {!stepOneDone && !fetching && (
              <button
                type="button"
                onClick={() => { setSkipped(true); onSkip?.() }}
                className="text-xs text-gray-500 dark:text-gray-400 underline hover:no-underline"
              >
                Skip and map anyway
              </button>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 mt-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800 dark:text-amber-200">{error}</p>
            </div>
          )}

          {/* What was actually stored — the point of making this step explicit. */}
          {result && (
            <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                <Database className="h-3.5 w-3.5 text-gray-400" />
                <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  Stored locally
                </span>
                {result.total_records != null && (
                  <span className="ml-auto text-[11px] text-gray-400">
                    {result.total_records.toLocaleString()} records
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 px-3 py-2.5">
                {lookupRows.map(r => (
                  <div key={r.key} className="flex items-baseline justify-between gap-2 min-w-0">
                    <span className="text-xs text-gray-600 dark:text-gray-400 truncate">{r.label}</span>
                    <span className={`text-xs font-mono font-semibold flex-shrink-0 ${r.count > 0
                      ? 'text-gray-900 dark:text-gray-100'
                      : 'text-amber-600 dark:text-amber-400'}`}>
                      {r.count.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
              <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30">
                <p className="text-xs text-gray-700 dark:text-gray-300">
                  <strong>{result.validations?.rules_stored ?? 0}</strong> validation
                  {(result.validations?.rules_stored ?? 0) !== 1 ? 's' : ''} stored
                  {result.validations?.mandatory != null && (
                    <> · <strong>{result.validations.mandatory}</strong> mandatory</>
                  )}
                  {result.validations?.sample_size != null && (
                    <> · from the last {result.validations.sample_size} {nouns.plural}</>
                  )}
                </p>
                {result.validations?.new_fields?.length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    New fields found: {result.validations.new_fields.join(', ')}
                  </p>
                )}
              </div>
            </div>
          )}
        </StepHeader>
      </div>

      {/* ── Step 2 ─────────────────────────────────────────────────────────── */}
      <div className={`rounded-xl border p-4 ${stepTwoUnlocked
        ? 'border-gray-200 dark:border-gray-700'
        : 'border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-900/20'}`}>
        <StepHeader
          number={2}
          title={`Upload a ${docName} PDF to map fields`}
          state={stepTwoUnlocked ? 'active' : 'locked'}
        >
          {stepTwoUnlocked ? (
            <>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 mb-3">
                AI reads the document and maps each field it finds to a NetSuite
                field key{skipped ? '' : ', using the field set fetched in step 1'}.
              </p>
              {renderStepTwo?.()}
            </>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Complete step 1 first. Mapping needs to know which NetSuite fields
              exist and which are mandatory before it can map a document to them.
            </p>
          )}
        </StepHeader>
      </div>
    </div>
  )
}
